/**
 * Supported locales for Paypercut Checkout
 *
 * @remarks
 * Single source of truth for all supported locale codes.
 *
 * Supported languages:
 * - Bulgarian: 'bg', 'bg-BG'
 * - English: 'en', 'en-GB'
 * - Greek: 'el', 'el-GR'
 * - Romanian: 'ro', 'ro-RO'
 * - Croatian: 'hr', 'hr-HR'
 * - Polish: 'pl', 'pl-PL'
 * - Czech: 'cs', 'cs-CZ'
 * - Slovenian: 'sl', 'sl-SI'
 * - Slovak: 'sk', 'sk-SK'
 *
 * @example
 * ```typescript
 * const checkout = PaypercutCheckout({
 *   locale: 'bg'  // or 'bg-BG', 'en', 'en-GB', etc.
 * });
 * ```
 */
export const LOCALES = [
  'bg', 'bg-BG',
  'en', 'en-GB',
  'el', 'el-GR',
  'ro', 'ro-RO',
  'hr', 'hr-HR',
  'pl', 'pl-PL',
  'cs', 'cs-CZ',
  'sl', 'sl-SI',
  'sk', 'sk-SK',
  'hu', 'hu-HU',
] as const;

/**
 * Locale type - union of all supported locale codes plus 'auto'
 * @example
 * ```typescript
 * const locale: Locale = 'bg';
 * const lang: Locale = 'bg';
 * const autoLocale: Locale = 'auto';
 * ```
 */
export type Locale = typeof LOCALES[number] | 'auto';

/**
 * Fast runtime check using Set for O(1) lookup
 * @internal
 */
const LOCALE_SET: ReadonlySet<string> = new Set(LOCALES);

/**
 * Normalize and validate locale
 *
 * @param locale - Locale code to normalize
 * @returns Normalized locale code or 'en' as fallback
 *
 * @remarks
 * - 'auto' or empty → 'en'
 * - Unsupported locale → 'en' with console warning
 * - Supported locale → original value
 *
 * @example
 * ```typescript
 * normalizeLocale('auto')  // → 'en'
 * normalizeLocale('bg')    // → 'bg'
 * normalizeLocale('de')    // → 'en' (with warning)
 * ```
 */
export function normalizeLocale(locale: string | Locale): typeof LOCALES[number] | 'en' {
  if (!locale || locale === 'auto') return 'en';
  if (LOCALE_SET.has(locale)) return locale as typeof LOCALES[number];
  console.warn(`[PaypercutCheckout] Locale "${locale}" is not supported. Falling back to "en".`);
  return 'en';
}

